import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Client } from 'src/users';
import { useFlags } from 'src/feature-flags/FlagsHook';
import DrawerFlow, { DrawerFlowStep } from 'src/UI/drawer-flow/DrawerFlow';
import SelectSourceStep from './steps/SelectSourceStep';
import MeetingNoteSelectTemplateStep from './steps/MeetingNoteSelectTemplateStep';
import SelectTemplateTypeStep from './steps/SelectSourceTypeStep';
import SelectAgendaStep from './steps/SelectAgendaStep';
import MeetingNoteProgressOneSvg from './assets/meeting-note-progress-one.svg?react';
import MeetingNoteProgressTwoSvg from './assets/meeting-note-progress-two.svg?react';
import MeetingNoteProgressThreeSvg from './assets/meeting-note-progress-three.svg?react';
import MeetingNoteSelectClientStep from './steps/MeetingNoteSelectClientStep';

type MeetingNoteFlowProps = {
  opened: boolean;
  close: () => void;
};

export type MeetingNoteTemplateType = 'template' | 'agenda';

export type MeetingNoteSourceType = 'audio' | 'document';

export type MeetingNoteData = {
  client?: Client;
  templateType?: MeetingNoteTemplateType;
  selectedRecordingId?: string;
  selectedSourceType?: MeetingNoteSourceType;
  selectedTemplateId?: string;
  selectedAgendaFileId?: string;
  summarise?: boolean;
};

const initialData: MeetingNoteData = {
  client: undefined,
  templateType: undefined,
  selectedRecordingId: undefined,
  selectedSourceType: undefined,
  selectedTemplateId: undefined,
  selectedAgendaFileId: undefined,
  summarise: true,
};

const getSteps = (
  featureFlagEnabled: boolean,
  templateType?: MeetingNoteTemplateType,
): DrawerFlowStep<MeetingNoteData>[] => {
  const baseSteps: DrawerFlowStep<MeetingNoteData>[] = [
    {
      title: 'Select client',
      showClientName: false,
      screens: [
        {
          component: MeetingNoteSelectClientStep,
          renderProgress: () => <MeetingNoteProgressOneSvg />,
        },
      ],
    },
    {
      title: 'Select source',
      clientNamePrefix: 'for',
      screens: [
        {
          component: SelectSourceStep,
          renderProgress: () => <MeetingNoteProgressTwoSvg />,
        },
      ],
    },
  ];

  if (featureFlagEnabled) {
    baseSteps.push({
      title: 'Select structure',
      clientNamePrefix: 'for',
      screens: [
        {
          component: SelectTemplateTypeStep,
          renderProgress: () => <MeetingNoteProgressTwoSvg />,
        },
      ],
    });

    if (templateType === 'agenda') {
      baseSteps.push({
        title: 'Select agenda',
        clientNamePrefix: 'for',
        screens: [
          {
            component: SelectAgendaStep,
            renderProgress: () => <MeetingNoteProgressThreeSvg />,
          },
        ],
      });
    } else if (templateType === 'template') {
      baseSteps.push({
        title: 'Select template',
        clientNamePrefix: 'for',
        screens: [
          {
            component: MeetingNoteSelectTemplateStep,
            renderProgress: () => <MeetingNoteProgressThreeSvg />,
          },
        ],
      });
    }
  } else {
    baseSteps.push({
      title: 'Select template',
      clientNamePrefix: 'for',
      screens: [
        {
          component: MeetingNoteSelectTemplateStep,
          renderProgress: () => <MeetingNoteProgressThreeSvg />,
        },
      ],
    });
  }

  return baseSteps;
};

export default function MeetingNoteFlow({ opened, close }: MeetingNoteFlowProps) {
  const navigate = useNavigate();
  const [data, setData] = useState<MeetingNoteData>(initialData);
  const { flagEnabledMeetingNoteFromAgenda } = useFlags();

  const steps = useMemo(
    () => getSteps(flagEnabledMeetingNoteFromAgenda ?? false, data.templateType),
    [flagEnabledMeetingNoteFromAgenda, data.templateType],
  );

  const onSuccess = useCallback(
    (data: MeetingNoteData) => {
      const clientId = data.client?.id;
      navigate(`/client/${clientId}/document/meeting-notes`);
    },
    [navigate],
  );

  return (
    <DrawerFlow
      opened={opened}
      close={close}
      title="Meeting note"
      steps={steps}
      initialData={initialData}
      data={data}
      setData={setData}
      onSuccess={onSuccess}
    />
  );
}
